/**
 * Production-safe logger utility
 * - Automatically silences debug logs in production builds
 * - Preserves error/warn logging for critical issues
 * - localStorage flags for troubleshooting:
 *   - VOLUNTAS_DEBUG: show debug/info in production (enableDebug() / disableDebug())
 *   - VOLUNTAS_DEBUG_CURRENCY: see docs/features/currency/plan-reduce-console-logging.md
 */

/* eslint-disable no-console */

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

class Logger {
  private isDevelopment: boolean;
  private forceDebug: boolean;

  constructor() {
    this.isDevelopment = process.env.NODE_ENV === 'development';
    // Allow forcing debug mode in production via localStorage
    this.forceDebug = typeof window !== 'undefined' && 
      localStorage.getItem('VOLUNTAS_DEBUG') === 'true';
  }

  private shouldLog(level: LogLevel): boolean {
    // Always show errors and warnings
    if (level === 'error' || level === 'warn') return true;
    
    // Show debug/info only in development or when forced
    return this.isDevelopment || this.forceDebug;
  }

  debug(...args: any[]): void {
    if (this.shouldLog('debug')) {
      console.log('[DEBUG]', ...args);
    }
  }

  info(...args: any[]): void {
    if (this.shouldLog('info')) {
      console.log('[INFO]', ...args);
    }
  }

  warn(...args: any[]): void {
    if (this.shouldLog('warn')) {
      console.warn('[WARN]', ...args);
    }
  }

  error(...args: any[]): void {
    // Always log errors
    console.error('[ERROR]', ...args);
  }

  // Helper to enable debug logging in production
  enableDebug(): void {
    if (typeof window !== 'undefined') {
      localStorage.setItem('VOLUNTAS_DEBUG', 'true');
      window.location.reload();
    }
  }

  disableDebug(): void {
    if (typeof window !== 'undefined') {
      localStorage.removeItem('VOLUNTAS_DEBUG');
      window.location.reload();
    }
  }
}

// Export singleton instance
export const logger = new Logger();

// Also export class for testing
export { Logger };